import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Input,
  isNullish,
  Modal,
  Radio
} from "@theorgchartdev/storybook";
import SectionPrompt from "@/appComponents/Blocks/Composites/SectionPrompt/SectionPrompt";
import { appstr } from "@coreapp/utils/appstr";
import { useEffect, useState } from "react";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { QChartMetadata } from "@coreapp/CoreApp/QChartMetadata";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import {
  sanitizeBaseChartList,
  useAccountSettingsContext
} from "@/providers/AccountSettingsProvider";
import { useToast } from "@/providers/ToastProvider";
import { CardButton } from "@/appComponents/Blocks/Composites/CardButton/CardButton";
import { ImportTypes } from "@/Utils/Constants";
import { DataSourceSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSourceSection";
import * as React from "react";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { ChangeChartTypePopover } from "@/appComponents/Blocks/Modals/CreateBaseChartModal/ChangeChartTypePopover";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import {
  enumFeaturesList,
  enumObjectTypes,
  enumPositionDataIngestMode,
  enumPositionGenerationMode,
  enumUIModes,
  kDefaultEmployeeViews,
  kDefaultPositionViews
} from "@coreapp/constants";
import UpgradePopover from "../../Composites/UpgradePopover/UpgradePopover";
import { getFeatureAccess } from "@/Utils/Misc";
import { useTopic } from "@/hooks/useTopic";
import { GetWSMgr } from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";

export const CreateBaseChartModal = ({ show, onClose }) => {
  const { setChartConfig, chartConfigRef, updateChartConfigRef, launchChartSettings } =
    useChartSettingsContext();
  const { accountConfig, SaveAccountConfigChanges, LoadAccountMetadata } =
    useAccountSettingsContext();
  const { canAccessPosMgmt, showPricingAndPackaging } = usePermissionCheck();
  const { addToast } = useToast();

  const [chartType, setChartType] = useState(null);
  const [customName, setCustomName] = useState("");
  const [defaultName, setDefaultName] = useState("");
  const [validation, setValidation] = useState({ isValid: true, status: "default", message: "" });
  const [positionConfiguration, setPositionConfiguration] = useState(null); // State to track the position configuration

  useEffect(() => {
    if (!show) return;

    const initializeModal = async () => {
      if (!accountConfig) {
        await LoadAccountMetadata();
      }
      handleInitialization();
    };

    initializeModal();

    return () => {
      handleReset();
    };
  }, [show, accountConfig]);

  const handleInitialization = () => {
    const baseChart = initializeNewBaseChart();
    const startingName = baseChart.name;
    setCustomName(startingName);
    setDefaultName(startingName);
    setChartConfig(baseChart);
    updateChartConfigRef(baseChart);
    setPositionConfiguration(new PositionConfig(baseChart));
    if (!canAccessPosMgmt) {
      setChartType("employee"); // Default to employee if position management is not accessible
    }
  };

  const handleReset = () => {
    updateChartConfigRef(null);
    setChartType(null);
    setCustomName("");
    setDefaultName("");
    setValidation({ status: "default", message: "", isValid: true });
    setPositionConfiguration(null);
  };

  const getNextChartID = () => {
    //if no charts exist, start with ID 0
    if (accountConfig?.charts.length === 0) {
      return "0";
    }
    const existingIDs = accountConfig.charts.map(chart => chart.ID);
    let validID = "0";

    while (existingIDs.includes(validID)) {
      validID = (parseInt(validID) + 1).toString();
    }

    return validID;
  };

  const initializeNewBaseChart = () => {
    const startingName = getDefaultNewChartName();
    const chartID = getNextChartID();
    const chartMetadata = new QChartMetadata();
    chartMetadata.SetDefaultValues();
    chartMetadata.fields = null;
    const newChartObj = chartMetadata.GetMetadataObject();
    newChartObj.ID = chartID;
    newChartObj.name = startingName;
    newChartObj.fullChart = true;
    newChartObj.import = { source: "", datadir: "", datafile: "" };
    return newChartObj;
  };

  const handleConfigUpdate = updatedChartConfig => {
    const nextChartConfig = { ...chartConfigRef.current, ...updatedChartConfig };
    const nextPositionConfig = new PositionConfig(nextChartConfig);
    if (positionConfiguration) {
      if (nextPositionConfig.isPositionDataIngestMultiple() && nextChartConfig.import.holders) {
        nextChartConfig.import = { ...nextChartConfig.import, ...nextChartConfig.import.holders };
      }
      setPositionConfiguration(nextPositionConfig);
    }
    updateChartConfigRef(nextChartConfig);
  };

  const handleCreate = async () => {
    const trimmedName = customName.trim();
    const updatedChartConfig = {
      ...chartConfigRef.current,
      name: trimmedName
    };

    updateChartConfigRef(updatedChartConfig);

    const isFirstChart = accountConfig?.charts.length === 0;

    if (isFirstChart) {
      const wsMgr = GetWSMgr();
      if (wsMgr) {
        handleClose();
        wsMgr.CreateInitialChart(updatedChartConfig);
      }
    } else {
      // Regular chart creation flow for non-first charts
      const updatedCharts = { charts: [...accountConfig.charts, chartConfigRef.current] };
      const sanitizedCharts = sanitizeBaseChartList(updatedCharts);
      await SaveAccountConfigChanges(sanitizedCharts);
      launchChartSettings(chartConfigRef.current.ID);
      addToast({
        ...createBoldToastMessage("Entity created", { tableName: trimmedName }),
        status: "success",
        timeoutMS: 5000
      });
      const wsMgr = GetWSMgr();
      wsMgr?.GetUsageTracker().ReportEvent("account_settings.base_chart.created", {
        chart_type: chartConfigRef.current.positionChart?.enabled ? "position" : "employee"
      });
      handleClose();
    }
  };

  const handleInput = e => {
    const inputValue = e.target.value;

    if (inputValue.length > 250) {
      return;
    }

    const trimmedValue = inputValue.trim();
    setCustomName(inputValue);

    if (!trimmedValue) {
      setValidation({ status: "error", message: appstr("Name cannot be blank"), isValid: false });
      return;
    }

    if (inputValue.length >= 150) {
      setValidation({
        status: "error",
        message: appstr("characters remaining", { count: (250 - inputValue.length).toString() }),
        isValid: true
      });
      return;
    }

    const isNameInUse = accountConfig.charts.some(
      chart => chart.name.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (isNameInUse) {
      setValidation({ status: "error", message: appstr("Name already in use"), isValid: false });
    } else {
      setValidation({ status: "default", message: "", isValid: true });
    }
  };

  const getDefaultNewChartName = () => {
    const existingTableNames = accountConfig ? accountConfig.charts.map(item => item.name) : [];
    return existingTableNames.GetUniqueDuplicateName(appstr("New Base Chart"), "");
  };

  const handleClose = () => {
    onClose();
  };

  const handleChartTypeChange = (type, check = true) => {
    if (type === chartType) return;

    if (chartType != null) {
      let updatedConfig = { ...chartConfigRef.current };
      if (check && dataSourceSet()) return;

      if (
        chartType === "position" &&
        positionConfiguration &&
        positionConfiguration.isPositionChart()
      ) {
        const posConfig = new PositionConfig(updatedConfig);
        updatedConfig.positionChart = {
          ...posConfig.positionConfig,
          enabled: false
        };
      }
      updateChartConfigRef(updatedConfig);
    }

    let updatedConfig = { ...chartConfigRef.current };

    if (type === "position") {
      const posConfig = new PositionConfig(updatedConfig);
      if (!updatedConfig.positionChart) {
        updatedConfig.positionChart = posConfig.positionConfig;
        updatedConfig.positionChart.enabled = true;
      } else {
        updatedConfig.positionChart.enabled = true;
        updatedConfig.positionChart.dataIngestionMode = enumPositionDataIngestMode.single;
        updatedConfig.positionChart.positionGenerationMode = enumPositionGenerationMode.derived;
      }
      // Set position chart views
      updatedConfig.views = [...kDefaultPositionViews];
      // Default nameusing to the position title mapping key and tag it as a position field
      updatedConfig.subchartrule = {
        ...updatedConfig.subchartrule,
        nameusing: updatedConfig.fields?.["Position Title"] ?? "Position Title",
        nameusingFieldType: enumObjectTypes.position
      };
    } else if (type === "employee") {
      // Set employee chart views
      updatedConfig.views = [...kDefaultEmployeeViews];
      // Reset nameusing to the employee title mapping key
      const { nameusingFieldType: _, ...restSubchart } = updatedConfig.subchartrule ?? {};
      updatedConfig.subchartrule = {
        ...restSubchart,
        nameusing: updatedConfig.fields?.["Title"] ?? "Title"
      };
    }

    // reset data source
    updatedConfig.import = { source: "", datadir: "", datafile: "" };

    updateChartConfigRef(updatedConfig);
    setPositionConfiguration(new PositionConfig(updatedConfig));
    setChartType(type);
  };

  const handleChangeDataIngestMode = e => {
    const nextDataIngestMode = e.target.value;
    if (nextDataIngestMode === chartConfigRef.current?.positionChart?.dataIngestionMode) return;
    let updatedConfig = { ...chartConfigRef.current };
    updatedConfig.import = { source: "", datadir: "", datafile: "" };
    updatedConfig.positionChart.dataIngestionMode = e.target.value;
    updatedConfig.positionChart.positionGenerationMode =
      nextDataIngestMode === enumPositionDataIngestMode.single
        ? enumPositionGenerationMode.derived
        : enumPositionGenerationMode.native; // forced for now
    setPositionConfiguration(new PositionConfig(updatedConfig));
    updateChartConfigRef(updatedConfig);
  };

  const isSourceSet = obj => {
    if (!obj || isNullish(obj) || isNullish(obj.source) || obj.source === "") {
      return false;
    }
    return true;
  };

  const dataSourceSet = () => {
    const chart = { ...chartConfigRef.current };
    if (chartType === "position") {
      if (!isSourceSet(chart.import)) return false;
      if (positionConfiguration.isPositionDataIngestMultiple()) {
        return (
          chart.import.holders &&
          isSourceSet(chart.import.holders) &&
          chart.import.positions &&
          isSourceSet(chart.import.positions)
        );
      }
    } else {
      return isSourceSet(chart.import);
    }
    return true;
  };

  const isValidConfiguration = () => {
    if (!validation.isValid) {
      return false;
    }

    if (isNullish(chartType)) {
      return false;
    }

    if (!dataSourceSet()) {
      return false;
    }

    return true;
  };

  const employeeCard = (
    <CardButton
      title={appstr("Employee Chart")}
      description={appstr("Employee Chart Details")}
      icon={"image-user"}
      isSelected={chartType === "employee"}
      onClick={() => handleChartTypeChange("employee")}
      dataTestId="account-settings-base-charts-create-type-employee-btn"
    />
  );
  const positionCard = (
    <CardButton
      title={appstr("Position Chart")}
      description={appstr("Position Chart Details")}
      icon={"cubes"}
      isSelected={chartType === "position"}
      onClick={() => handleChartTypeChange("position")}
      disabled={!canAccessPosMgmt}
      dataTestId="account-settings-base-charts-create-type-position-btn"
    />
  );

  const positionUpgradeText = {
    message: appstr(
      "Position Management enables you to visualize your employees and positions separately to gain better insight in your open roles and key position details"
    )
  };

  const positionUpgradeButton = (
    <CardButton
      title={appstr("Position Chart")}
      description={appstr("Position Chart Details")}
      upgradeIcon={true}
      pillTestId="create-base-chart-position-upgrade-pill"
      onClick={() => {}}
      dataTestId="account-settings-base-charts-create-type-upgrade-btn"
    />
  );

  const positionUpgradeCard = (
    <UpgradePopover
      popoverText={positionUpgradeText}
      customTrigger={positionUpgradeButton}
      featureKey={enumFeaturesList.positionManagement}
    />
  );

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "650px" }}
      >
        <DialogTitle
          divider={true}
          onClose={handleClose}
          data-testid="account-settings-base-charts-create-modal-close-btn"
        >
          {appstr("Create base chart")}
        </DialogTitle>
        <DialogContent customContentClass={"oc-build-chart-content"} divider={true}>
          <Input
            id={"builder-name-input"}
            dataTestId="account-settings-base-charts-create-name-input"
            key={"name-plan-input"}
            inputOnly={false}
            label={"Chart name"}
            placeholder={"Enter chart name"}
            value={customName}
            required
            size={"medium"}
            status={validation.status}
            message={validation.message}
            onChange={handleInput}
            require
          />
          <Divider />
          <SectionPrompt title={appstr("What type of chart are you creating")} require>
            <div className="card-button-container">
              {canAccessPosMgmt && dataSourceSet() && chartType === "position" ? (
                <ChangeChartTypePopover
                  handleChange={() => handleChartTypeChange("employee", false)}
                  reference={employeeCard}
                  dataTestPrefix="account-settings-base-charts-change-type-employee"
                />
              ) : (
                employeeCard
              )}
              {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.positionManagement) ? (
                // Show upgrade card when pricing and packaging is enabled and position management is not in features
                positionUpgradeCard
              ) : // Otherwise show regular position card logic
              !canAccessPosMgmt ||
                !getFeatureAccess(enumFeaturesList.positionManagement) ? null : dataSourceSet() &&
                chartType === "employee" ? (
                <ChangeChartTypePopover
                  handleChange={() => handleChartTypeChange("position", false)}
                  reference={positionCard}
                  dataTestPrefix="account-settings-base-charts-change-type-position"
                />
              ) : (
                positionCard
              )}
            </div>
          </SectionPrompt>
          <Divider />
          <SectionPrompt
            title={appstr("Main Data Source")}
            require
            description={
              chartType === "position" ? appstr("Choose what type of position data") : undefined
            }
          >
            {chartConfigRef.current && (
              <Flex direction={"column"} gap={"16px"}>
                {chartType === "position" && (
                  <Flex direction={"column"} gap={"12px"}>
                    <Radio
                      key={0}
                      data-testid={"data-ingest-radio--single"}
                      id={"data-ingest-radio--single"}
                      label={appstr("I have a single source with employee data")}
                      value={enumPositionDataIngestMode.single}
                      checked={
                        chartConfigRef.current?.positionChart?.dataIngestionMode ===
                        enumPositionDataIngestMode.single
                      }
                      onChange={handleChangeDataIngestMode}
                    />
                    <Radio
                      key={1}
                      data-testid={"data-ingest-radio--multi--single-source"}
                      id={"data-ingest-radio--multi--single-source"}
                      label={appstr("I have a single source with employee and position data")}
                      value={enumPositionDataIngestMode.multiSingle}
                      checked={
                        chartConfigRef.current?.positionChart?.dataIngestionMode ===
                        enumPositionDataIngestMode.multiSingle
                      }
                      onChange={handleChangeDataIngestMode}
                    />
                    <Radio
                      key={2}
                      data-testid={"data-ingest-radio--multi"}
                      id={"data-ingest-radio--multi"}
                      label={appstr("I have multiple sources with employee and position data")}
                      value={enumPositionDataIngestMode.multi}
                      checked={
                        chartConfigRef.current?.positionChart?.dataIngestionMode ===
                        enumPositionDataIngestMode.multi
                      }
                      onChange={handleChangeDataIngestMode}
                    />
                  </Flex>
                )}
                <DataSourceSection
                  type={ImportTypes.import}
                  sourceDataTestId={key => `data-source-opt-${ImportTypes.import}-${key}`}
                  chartConfig={chartConfigRef.current}
                  onChange={handleConfigUpdate}
                  triggerProps={{
                    disabled: isNullish(chartType),
                    tooltipProps: {
                      message: isNullish(chartType)
                        ? appstr("Select a chart type to configure data source")
                        : appstr("Change data source")
                    }
                  }}
                />
              </Flex>
            )}
          </SectionPrompt>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            onClick={handleClose}
            dataTestId="account-settings-base-charts-create-cancel-btn"
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleCreate}
            dataTestId={"builder-create-base-chart-btn"}
            disabled={!isValidConfiguration()}
          >
            {appstr("Create")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export const CreateBaseChartModalWrapper = () => {
  const { LoadAccountMetadata } = useAccountSettingsContext();
  const [showCreateChartModal, setShowCreateChartModal] = useState(false);

  useTopic("CreateChartModal", async show => {
    if (show) {
      // Load account metadata before showing the modal
      await LoadAccountMetadata();
      setShowCreateChartModal(true);
    }
  });

  if (!showCreateChartModal) return null;

  return (
    <CreateBaseChartModal
      show={showCreateChartModal}
      onClose={() => setShowCreateChartModal(false)}
    />
  );
};
